import { useRef, useState } from 'react';
import {
  Field,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@monksflow/monks-ui';
import { ImageIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Dropzone } from '@/shared/ui/Dropzone/Dropzone';
import { getFileDownloadUrl } from '@/shared/api/assets.api';
import { useImageUpload } from '@/shared/hooks/useImageUpload';
import { i18n } from '@/shared/i18n';

/**
 * Resolve a signed download URL for an asset id, keyed and cached by TanStack Query so it
 * cancels/deduplicates on its own. Replaces the old `useEffect(cancelled=false)` pattern in this
 * file — no direct effects, no manual cancellation flags.
 */
function useAssetDownloadUrl(assetId: string | null | undefined): string | null {
  const { data } = useQuery({
    queryKey: ['image-source-picker/download-url', assetId],
    queryFn: () => getFileDownloadUrl(assetId as string),
    enabled: !!assetId,
    staleTime: Infinity,
  });
  return data ?? null;
}
import type {
  ImageSource,
  ImageSourcePickerProps,
  ImageSourcePickerValue,
  UseKnowledgeBaseImagesHook,
} from './ImageSourcePicker.types';
import { useTranslation } from 'react-i18next';

// Order is the tab order, and the first entry is also where `initialSourceFor` lands with no seed
// value — so uploading, the most common intent, is both first and default.
const ALL_SOURCES: ImageSource[] = ['upload', 'kb', 'url'];

// Getters, not values: this map is built while the module is evaluated, so resolving per read
// is what lets a tab label follow a language change instead of freezing at import.
const SOURCE_LABELS: Record<ImageSource, string> = {
  get url() {
    return i18n.t('common:components.imageSourcePicker.imageUrl');
  },
  get kb() {
    return i18n.t('common:components.imageSourcePicker.knowledgeBase');
  },
  get upload() {
    return i18n.t('common:components.imageSourcePicker.upload');
  },
};

const noopUseKnowledgeBaseImages: UseKnowledgeBaseImagesHook = () => ({
  knowledgeBases: [],
  imageFiles: [],
  filesLoading: false,
});

const PREVIEW_FRAME = 'flex justify-center rounded border border-border p-2';

/** Pick the initial active tab from the seed value, falling back to the first available source. */
function initialSourceFor(
  value: ImageSourcePickerValue | null,
  sources: ImageSource[],
): ImageSource {
  if (value?.kind === 'url' && sources.includes('url')) return 'url';
  if (value?.kind === 'asset') {
    if (sources.includes('kb')) return 'kb';
    if (sources.includes('upload')) return 'upload';
  }
  return sources[0] ?? 'url';
}

/**
 * Shared image-source selector with Upload / Knowledge Base / URL tabs. State is owned
 * internally and reported through `onChange`; remount via a React `key` to reset when the edited
 * element changes. Backs the shared `ImageEditDialog` (used by the templates-config edit mode and
 * the email widget), which each pass the `sources` they want.
 *
 * Refactor note: `onChange` used to be pushed via a `useEffect` that watched the
 * internal state trio. That fired an extra `null` on mount and could re-fire on unrelated parent
 * rerenders. It is now called exactly once per user action, straight from the action's handler.
 * A separate effect used to resolve the preview URL for the selected asset — replaced by the
 * local `useAssetDownloadUrl`, a `useQuery` that handles cancellation and caching.
 */
export function ImageSourcePicker({
  sources = ALL_SOURCES,
  initialValue = null,
  onChange,
  useKnowledgeBaseImagesHook = noopUseKnowledgeBaseImages,
}: ImageSourcePickerProps) {
  const { t } = useTranslation('common');

  const [imageSource, setImageSource] = useState<ImageSource>(() =>
    initialSourceFor(initialValue, sources),
  );
  const [imageUrl, setImageUrl] = useState(initialValue?.kind === 'url' ? initialValue.url : '');
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(
    initialValue?.kind === 'asset' ? initialValue.assetId : null,
  );
  const [selectedKbId, setSelectedKbId] = useState<string | null>(null);

  const { knowledgeBases, imageFiles, filesLoading } = useKnowledgeBaseImagesHook(selectedKbId);
  const { upload, isUploading } = useImageUpload();
  const previewUrl = useAssetDownloadUrl(selectedAssetId);

  // Notify parents from handlers. Each helper commits new internal state AND calls `onChange`
  // in the same tick, so callers get exactly one call per user action.
  const commitUrl = (nextUrl: string) => {
    setImageUrl(nextUrl);
    onChange(nextUrl ? { kind: 'url', url: nextUrl } : null);
  };

  const commitAsset = (nextAssetId: string | null) => {
    setSelectedAssetId(nextAssetId);
    onChange(nextAssetId ? { kind: 'asset', assetId: nextAssetId } : null);
  };

  /**
   * Re-derive the reported value for a tab from the state that tab actually owns: `url` reads
   * `imageUrl`, while `kb` and `upload` both read `selectedAssetId`.
   */
  const valueForSource = (source: ImageSource): ImageSourcePickerValue | null => {
    if (source === 'url') return imageUrl ? { kind: 'url', url: imageUrl } : null;
    return selectedAssetId ? { kind: 'asset', assetId: selectedAssetId } : null;
  };

  const committedSourceRef = useRef(imageSource);

  const handleTabChange = (next: ImageSource) => {
    // Radix reports the change twice for a single click — once when the trigger takes focus
    // (automatic activation) and once on the click itself. Both land in the same batch, so
    // `imageSource` still reads the old tab on the second one; the guard has to be a ref,
    // which updates synchronously. Without it the caller sees two identical emissions for
    // one user action.
    if (next === committedSourceRef.current) return;
    committedSourceRef.current = next;
    setImageSource(next);
    // A tab switch IS a value change: only the active tab's state is visible and editable, so
    // the caller must see what the destination tab holds — `null` when it holds nothing. Without
    // this, a URL typed on the URL tab survived a switch to an empty Upload tab and callers such
    // as `ConfigureContentElementModal` (whose Save is gated on the emitted value) would happily
    // save the abandoned URL. Emitting from the handler keeps this an event-handler notification
    // rather than
    // the per-active-tab `useEffect` this file used to carry.
    onChange(valueForSource(next));
  };

  const handleImageDrop = async (files: File[]) => {
    if (files.length === 0) return;
    const assetId = await upload(files[0]);
    setImageSource('upload');
    commitAsset(assetId);
  };

  const handleKbSelect = (nextKbId: string | null) => {
    setSelectedKbId(nextKbId);
    // Clearing the current asset when the KB changes is a real value change → notify.
    commitAsset(null);
  };

  return (
    <>
      <Tabs value={imageSource} onValueChange={(v) => handleTabChange(v as ImageSource)}>
        <TabsList>
          {sources.map((source) => (
            <TabsTrigger key={source} value={source}>
              {SOURCE_LABELS[source]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* URL tab */}
      {imageSource === 'url' && (
        <>
          <Field>
            <FieldLabel htmlFor="image-source-url">
              {t('components.imageSourcePicker.imageUrl')}
            </FieldLabel>
            <Input
              id="image-source-url"
              size="sm"
              placeholder="https://example.com/image.jpg"
              value={imageUrl}
              onChange={(e) => commitUrl(e.target.value)}
            />
          </Field>
          {imageUrl && (
            <div className={PREVIEW_FRAME}>
              <img
                src={imageUrl}
                alt={t('components.imageSourcePicker.preview')}
                className="max-h-50 max-w-full object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
        </>
      )}

      {/* Knowledge Base tab */}
      {imageSource === 'kb' && (
        <>
          <Select value={selectedKbId ?? ''} onValueChange={(v) => handleKbSelect(v || null)}>
            <SelectTrigger
              size="sm"
              label={t('components.imageSourcePicker.knowledgeBase')}
              aria-label={t('components.imageSourcePicker.knowledgeBase')}
            >
              <SelectValue placeholder={t('components.imageSourcePicker.knowledgeBase')} />
            </SelectTrigger>
            <SelectContent>
              {knowledgeBases.map((kb) => (
                <SelectItem key={kb.id} value={kb.id}>
                  {kb.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedKbId && (
            <div className="grid max-h-62.5 grid-cols-3 gap-2 overflow-auto p-1">
              {filesLoading ? (
                <p className="col-span-full py-8 text-center text-body-sm text-muted-foreground">
                  {t('components.imageSourcePicker.loadingImages')}
                </p>
              ) : imageFiles.length === 0 ? (
                <p className="col-span-full py-8 text-center text-body-sm text-muted-foreground">
                  {t('components.imageSourcePicker.noImagesFoundInThis')}
                </p>
              ) : (
                imageFiles.map((file) => (
                  <KbImageThumbnail
                    key={file.assetId}
                    file={file}
                    selected={selectedAssetId === file.assetId}
                    onSelect={() => commitAsset(file.assetId)}
                  />
                ))
              )}
            </div>
          )}

          {selectedAssetId && previewUrl && (
            <div className={PREVIEW_FRAME}>
              <img
                src={previewUrl}
                alt={t('components.imageSourcePicker.selected')}
                className="max-h-37.5 max-w-full object-contain"
              />
            </div>
          )}
        </>
      )}

      {/* Upload tab */}
      {imageSource === 'upload' && (
        <>
          <Dropzone
            onDrop={(files) => void handleImageDrop(files)}
            // Extensions, not copy: react-dropzone matches these against the drop
            // payload and the OS picker filter, so a translated value breaks upload.
            acceptedFormats={{
              'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
            }}
            title={
              isUploading
                ? t('components.imageSourcePicker.uploading')
                : t('components.imageSourcePicker.dropAnImageHereOr')
            }
            description={t('components.imageSourcePicker.supportsJpgPngWebP')}
            size="medium"
          />
          {selectedAssetId && previewUrl && (
            <div className={PREVIEW_FRAME}>
              <img
                src={previewUrl}
                alt={t('components.imageSourcePicker.uploaded')}
                className="max-h-37.5 max-w-full object-contain"
              />
            </div>
          )}
        </>
      )}
    </>
  );
}

// Small helper component for KB image thumbnails. Uses the local `useAssetDownloadUrl` (TanStack
// Query under the hood) to fetch and cache the signed URL — no roll-your-own cancellation needed.
function KbImageThumbnail({
  file,
  selected,
  onSelect,
}: {
  file: { assetId: string; fileName: string };
  selected: boolean;
  onSelect: () => void;
}) {
  const thumbUrl = useAssetDownloadUrl(file.assetId);

  return (
    <button
      type="button"
      onClick={onSelect}
      title={file.fileName}
      aria-pressed={selected}
      // Definite height (not `aspect-square`) so the grid's auto row tracks size correctly — with
      // an aspect ratio the row track stays at min-content and the (taller) cell overflows into
      // the next row, making thumbnails appear to overlap once `object-contain` exposes the gap.
      className={`flex h-24 cursor-pointer items-center justify-center overflow-hidden rounded border-2 bg-muted transition-colors duration-fast ease-out ${
        selected ? 'border-primary' : 'border-border hover:border-muted-foreground'
      }`}
    >
      {thumbUrl ? (
        <img
          src={thumbUrl}
          alt={file.fileName}
          // `contain` (not `cover`) so the whole image is visible in the square cell — KB images
          // are often landscape and `cover` would crop most of them away. Empty space is filled
          // by the cell's muted background.
          className="size-full object-contain"
        />
      ) : (
        <ImageIcon className="size-8 text-muted-foreground" />
      )}
    </button>
  );
}
